import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Video } from 'lucide-react';
import { z } from 'zod';
import { useSession as useAuthSession } from '@/lib/auth-client';
import { apiGet } from '@/lib/api-client';
import { VideoThumbnail } from '@/components/preview/video-thumbnail';
import { SessionGalleryItemSchema } from '@shared/schemas';

// ==============================================================================
// API TYPES & HOOK
// ==============================================================================

/**
 * Paginated sessions response schema (matches backend response)
 */
const SessionsListResponseSchema = z.object({
  items: z.array(SessionGalleryItemSchema),
  pagination: z.object({
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});

type SessionsListResponse = z.output<typeof SessionsListResponseSchema>;

/**
 * Hook for fetching user's sessions with pagination
 */
function useSessions(page: number = 1, limit: number = 12) {
  return useQuery<SessionsListResponse>({
    queryKey: ['sessions', 'list', page, limit],
    queryFn: async (): Promise<SessionsListResponse> => {
      const response = await apiGet(
        `/sessions?include=currentVersion&limit=${limit}&page=${page}`,
        SessionsListResponseSchema
      );
      // Cast to output type to ensure defaults are applied
      return response.data as SessionsListResponse;
    },
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: false,
  });
}

// ==============================================================================
// SESSION CARD COMPONENT
// ==============================================================================

type SessionGalleryItem = SessionsListResponse['items'][number];

interface SessionCardProps {
  session: SessionGalleryItem;
}

function SessionCard({ session }: SessionCardProps) {
  const title = session.title || 'Untitled';
  const createdDate = new Date(session.createdAt * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  // Build the link URL
  const linkUrl = session.currentVersionId
    ? `/create?session=${session.id}&version=${session.currentVersionId}`
    : `/create?session=${session.id}`;

  // Ensure preferences have proper defaults for VideoThumbnail
  const preferences = session.currentVersion?.preferences
    ? {
        fps: session.currentVersion.preferences.fps ?? 30,
        width: session.currentVersion.preferences.width ?? 1920,
        height: session.currentVersion.preferences.height ?? 1080,
        durationInSeconds: session.currentVersion.preferences.durationInSeconds ?? 10,
      }
    : null;

  return (
    <Link
      to={linkUrl}
      className="group block rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden rounded-t-xl bg-slate-100">
        {session.currentVersion?.code && preferences ? (
          <VideoThumbnail
            code={session.currentVersion.code}
            componentName={session.currentVersion.componentName}
            preferences={preferences}
            className="h-full w-full"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Video className="h-8 w-8 text-slate-300" />
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4">
        {/* Title */}
        <h3 className="truncate text-sm font-medium text-slate-900">
          {title}
        </h3>

        {/* Meta Row */}
        <div className="mt-2 flex items-center justify-between">
          {/* Version Badge */}
          <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
            v{session.versionCount}
          </span>

          {/* Date */}
          <span className="text-xs text-slate-500">
            {createdDate}
          </span>
        </div>
      </div>
    </Link>
  );
}

// ==============================================================================
// SKELETON CARD COMPONENT
// ==============================================================================

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Thumbnail skeleton */}
      <div className="aspect-video animate-pulse rounded-t-xl bg-slate-100" />

      {/* Content skeleton */}
      <div className="p-4">
        <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200" />
        <div className="mt-2 flex items-center justify-between">
          <div className="h-5 w-10 animate-pulse rounded-md bg-slate-200" />
          <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
        </div>
      </div>
    </div>
  );
}

// ==============================================================================
// EMPTY STATE COMPONENT
// ==============================================================================

function EmptyState() {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
        <Video className="h-8 w-8 text-slate-400" />
      </div>
      <h3 className="text-sm font-medium text-slate-900">No videos yet</h3>
      <p className="mt-1 text-sm text-slate-500">
        Create your first video to get started
      </p>
      <Link
        to="/create"
        className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2"
      >
        <Plus className="h-4 w-4" />
        Create your first video
      </Link>
    </div>
  );
}

// ==============================================================================
// STORE PAGE COMPONENT
// ==============================================================================

export function StorePage() {
  const [page, setPage] = useState(1);
  const { data: authSession, isPending: isAuthPending } = useAuthSession();
  const { data, isLoading, isError } = useSessions(page);

  // Compute hasMore from pagination
  const hasMore = data ? data.pagination.page < data.pagination.totalPages : false;

  // Handle pagination
  const handleNextPage = useCallback(() => {
    if (hasMore) {
      setPage((p) => p + 1);
    }
  }, [hasMore]);

  const handlePrevPage = useCallback(() => {
    if (page > 1) {
      setPage((p) => p - 1);
    }
  }, [page]);

  // Show loading while checking auth
  if (isAuthPending) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="flex h-screen items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!authSession?.user) {
    // In development, redirect to backend landing page
    const isDev = import.meta.env.DEV;
    if (isDev) {
      window.location.href = `http://localhost:8787/?returnTo=${encodeURIComponent(window.location.origin + '/store')}`;
    } else {
      window.location.replace(window.location.origin);
    }
    return null;
  }

  const sessions = data?.items || [];
  const totalCount = data?.pagination?.total ?? 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: `
            linear-gradient(to right, #e2e8f0 1px, transparent 1px),
            linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Content */}
      <div className="relative">
        {/* Header */}
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="flex h-14 items-center justify-between px-6">
            {/* Left: Logo + Navigation */}
            <div className="flex items-center gap-6">
              <h1 className="text-lg font-semibold text-slate-900">Vemotion AI</h1>

              {/* Navigation Links */}
              <nav className="flex items-center gap-1">
                <Link
                  to="/create"
                  className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-slate-600 hover:bg-slate-50 hover:text-slate-700"
                >
                  Create
                </Link>
                <Link
                  to="/store"
                  className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-slate-100 text-slate-900"
                >
                  My Videos
                </Link>
              </nav>
            </div>
            <Link
              to="/create"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2"
            >
              <Plus className="h-4 w-4" />
              New Video
            </Link>
          </div>
        </header>

        {/* Gallery Grid */}
        <main className="mx-auto max-w-6xl px-4 py-8">
          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          )}

          {/* Error State */}
          {isError && !isLoading && (
            <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <p className="text-sm text-slate-500">Failed to load videos. Please try again.</p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !isError && sessions.length === 0 && <EmptyState />}

          {/* Sessions Grid */}
          {!isLoading && !isError && sessions.length > 0 && (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {sessions.map((session) => (
                  <SessionCard key={session.id} session={session} />
                ))}
              </div>

              {/* Pagination */}
              {(page > 1 || hasMore) && (
                <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
                  <p className="text-sm text-slate-500">
                    {totalCount > 0 ? `${totalCount} total videos` : ''}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={page === 1}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-2 text-sm text-slate-500">Page {page}</span>
                    <button
                      onClick={handleNextPage}
                      disabled={!hasMore}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
